import React, { Fragment, useContext, useCallback, useEffect, useState, useRef } from "react";
import Bulk from "./../Bulk/Bulk";
import Basket from "./../Basket/Basket";
import style from "./../Order.module.scss";
import { Container, Row, Col, Input, Label } from "reactstrap";
import { accountProps, customFetchAssetInfo, customFetchAccountInfo } from "./commons";
import {
  OrderPriceType,
  TimeFrame,
  ProductType,
  Type,
  Mode,
  OrderStatus,
} from "../../../../utils/Order/Order";
import { Field, Symbol, Account, Numeric, Combobox, TimeForce, PriceType } from "../Field/Field";
import { showToast, UserType } from "../../../../utils/Helper";
import { Dispatch } from "../store/store";
import { onChange, onAccountChange } from "../Order";
import QuantityHelper from "../Helper/QuantityHelper";
import GoalSeek from "../GoalSeek/GoalSeek";
import Booktype from "../Field/Booktype";
import { canOfferUseProcessDate, isValidResponse200 } from "../../../../utils/Order/OrderProcessDate";
import { getTodayISO, isFutureOrToday } from "../../../../utils";

const Bonds = ({
  maxDecimals,
  updatePrice,
  newSymbol,
  fetchCalculatorDataForOffers,
  booktypes,
  platformRole,
  fetchGetCalculator,
  opsViewAccountName,
  toggle,
}) => {
  const { dispatch, state, services } = useContext(Dispatch);
  const { order, fields, ui, entities } = state;
  const { editable, errors, readonly, hiddens } = fields;
  const [showAdvanced, setShowAdvanced] = useState(false);

  const initialOrderProcessDate = useRef(order.OrderProcessDate);
  const [orderProcessDate, setOrderProcessDate] = useState(order.OrderProcessDate || null);
  const [useProcessDateInOffers, setUseProcessDateInOffers] = useState(false);
  const userChangedProcessDate = useRef(false);

  const isFromOffer = order && order.offer && order.offer.OfferId != "0";
  const offerCanUseProcessDate = canOfferUseProcessDate({ order, services });
  const [canEditTNA, setCanEditTNA] = useState(true);

  const [showProcessDate, setShowProcessDate] = useState(false);

  useEffect(() => {
    if (order && order.AssetId && order.SubAssetClassAllowsProcessDate) {
      setShowProcessDate(true);
    } else
      setShowProcessDate(false);
  }, [order.AssetId, order.SubAssetClassAllowsProcessDate]);

  useEffect(() => {
    if (order.OrderProcessDate) setOrderProcessDate(order.OrderProcessDate);
  }, [order.OrderProcessDate]);

  useEffect(() => {
    if (!offerCanUseProcessDate) return;
    if (orderProcessDate == initialOrderProcessDate.current) return;
    // Solo recalcular si el usuario cambió la fecha manualmente desde el input.
    // Al abrir el modal, el precio de GetOffers ya es el correcto.
    if (!userChangedProcessDate.current) {
      initialOrderProcessDate.current = orderProcessDate;
      return;
    }
    // Clean price: nunca recalcular
    if (!order.AssetPricesDirty) {
      initialOrderProcessDate.current = orderProcessDate;
      return;
    }
    // Dirty + usuario cambió fecha → recalcular
    initialOrderProcessDate.current = orderProcessDate;

    const payload = {
      offerId: order.offer.OfferId,
      date: orderProcessDate,
    };

    services.account.fetchRecalculatePriceWithProcessDate(payload).then(responseWrapper => {
      const { response } = responseWrapper;
      if (isValidResponse200(response)) {
        const { newDirtyPrice } = response;
        receiveDirtyPrice(newDirtyPrice);
        showToast("The order process date has been recalculated and the price has been adjusted to the new process date.");
      } else showToast("An error occurred while attempting to recalculate the price for the new processing date.",true);
    })
  }, [orderProcessDate]);

  useEffect(() => {
    if (!offerCanUseProcessDate) return;
    if (isFutureOrToday(orderProcessDate)) return;

    const todayDate = getTodayISO();

    services.account
      .fetchGetNextValidWithParameters()
      .then((responseWrapper) => {
        const { response } = responseWrapper;
        if (response && response.initialDate && response.initialDate != "")
          updateProcessDate(response.initialDate);
        else updateProcessDate(todayDate);
      })
      .catch(() => updateProcessDate(todayDate));
  }, [])

  useEffect(() => {
    if (!isFromOffer || !services.account.getParameterValue) return;
     
    const payload = { ParameterName: "UseProcessDateInOffers" };

    services.account.getParameterValue(payload, response => {
      if (response && response.SdtParametersData) {
        setUseProcessDateInOffers(response.SdtParametersData.ParameterNumericValue == 1);
      }
   });
  }, [])

  useEffect(() => {
    if (!services.account.getParameterValue) return;

    const payload = { ParameterName: "CanEditTNA" };

    services.account.getParameterValue(payload, response => {
      if (response && response.SdtParametersData) {
        setCanEditTNA(response.SdtParametersData.ParameterNumericValue == 1);
      }
    });
  }, []);

  const updateProcessDate = (date) => {
    onChange(dispatch, { OrderProcessDate: date});
    setOrderProcessDate(date);
  }

  const onUserChangeProcessDate = (date) => {
    userChangedProcessDate.current = true;
    updateProcessDate(date);
  };

  const accountInfo = (props, updateOrderValues) =>
    customFetchAccountInfo(
      dispatch,
      services.account,
      {
        ...order,
        CurrentAccount: {
          ...order.CurrentAccount,
          ...props,
        },
      },
      updateOrderValues
    );

  let quantities = [];
  let quantityTitle = <span>Quantity</span>;
  if (ui.type == Type.Default || ui.type == Type.Chained || ui.type == Type.Cross) {
    if (order.CurrentAccount.Day) {
      quantities = order.CurrentAccount.Day.map((day) => {
        return {
          date: day.Date,
          status: day.Status,
          quantity: parseFloat(day.AssignedQty),
        };
      });
    }
    quantityTitle = (
      <Fragment>
        <span>Quantity</span>
        {quantities.length > 0 && order.OrderId > 0 && (
          <QuantityHelper quantities={quantities} name="general">
            <i className="icon-info icons" />
          </QuantityHelper>
        )}
      </Fragment>
    );
  }

  const receiveDirtyPrice = (v) => {
    onChange(dispatch, {
      [order.OrderType == OrderPriceType.LIMIT ||
      order.OrderType == OrderPriceType.MARKETONOPEN ||
      order.OrderType == OrderPriceType.MARKETONCLOSE
        ? "OrderPrice"
        : "OrderStopPrice"]: v,
    });
  };

  const receiveOriginalTNA = (v) => {
    onChange(dispatch, {
      ["ParentOrderOriginalNominalRate"]: v,
    });
  };
  
  return (
    <div className={style.form}>
      <Container>
        <Row>
          {/* Left */}
          <Col xs={12} lg={6}>
            {/* ##### Symbol ###### */}
            <Field label={"Symbol"} error={errors.AssetTicker || errors.AssetRefPrice}>
              <Symbol
                form={order.AssetClassForm}
                onChange={useCallback((v) => onChange(dispatch, v), [order.AssetTicker])}
                placeholder={"Symbol"}
                disabled={readonly.AssetTicker || (order.offer && +order.offer.OfferId > 0)}
                invalid={!!errors.AssetTicker}
                value={order.AssetTicker}
                description={order.AssetDescription}
                provider={order.AssetSecurityProvider}
                assetid={order.AssetId}
                attrId={"AssetId"}
                attrTicker={"AssetTicker"}
                attrDescription={"AssetDescription"}
                attrProvider={"AssetSecurityProvider"}
                {...services.symbol}
                assetInfo={() => customFetchAssetInfo(dispatch, services.symbol, order)}
                updatePrice={updatePrice}
                maxChar={30}
              />
            </Field>

            <Row>
              {/* ##### Trade Currency ###### */}
              <Col lg={6}>
                <Field label={"Trade Currency"} error={errors.OrderCurrencySymbol}>
                  <Input
                    type="text"
                    placeholder="Trade Currency"
                    value={order.OrderCurrencySymbol || ""}
                    disabled
                  />
                </Field>
              </Col>
              {/* ##### Settlement Currency ###### */}
              <Col lg={6}>
                <Field label={"Settlement Currency"} error={errors.OrderSettlementCurrencySymbol}>
                  <Combobox
                    value={order.OrderSettlementCurrencyId || 0}
                    items={entities.settlementCurrencies}
                    disabled={readonly.OrderSettlementCurrencySymbol}
                    invalid={!!errors.OrderSettlementCurrencySymbol}
                    onChange={(v) => onChange(dispatch, { OrderSettlementCurrencyId: v })}
                    emptyLabel={"Settlement Currency"}
                  />
                </Field>
              </Col>
            </Row>

            {/* ##### Quantity ###### */}
            <Field label={quantityTitle} error={errors.OrderQuantity}>
              <Numeric
                value={order.OrderQuantity}
                placeholder={"Quantity"}
                dataTypeDecimalName="quantity"
                disabled={readonly.OrderQuantity || ui.isBulk || ui.isBasket}
                onChange={(v) => onAccountChange(dispatch, { Quantity: v })}
                invalid={!!errors.OrderQuantity}
              />
            </Field>

            {/* ##### Price Type ###### */}
            <PriceType
              type={order.OrderType}
              form={ProductType.BOND}
              currency={order.OrderCurrencySymbol}
              values={{
                limPrice: order.OrderPrice,
                stopPrice: order.OrderStopPrice,
              }}
              oldValues={{
                limPrice: order.HasOriginalOrReplacingData
                  ? order.OriginalOrReplacingData.OrderPrice
                  : order.OrderPrice,
                stopPrice: order.HasOriginalOrReplacingData
                  ? order.OriginalOrReplacingData.OrderStopPrice
                  : order.OrderStopPrice,
              }}
              editable={{
                limPrice: editable.OrderPrice && order.OrderStatus === OrderStatus.ACKED,
                stopPrice: editable.OrderStopPrice && order.OrderStatus === OrderStatus.ACKED,
              }}
              errors={{
                type: errors.OrderType,
                limPrice: errors.OrderPrice,
                stopPrice: errors.OrderStopPrice,
              }}
              readonly={{
                type: readonly.OrderType,
                limPrice: readonly.OrderPrice,
                stopPrice: readonly.OrderStopPrice,
              }}
              maxDecimals={maxDecimals}
              newSymbol={newSymbol}
              onChange={(v) => onChange(dispatch, v)}
            />

            {order.OrderType == "Limit" && (
              <Col className="my-2 p-0">
                <p
                  className="d-inline"
                  style={{ cursor: "pointer", borderBottom: "1px solid #1058a1", color: "#1058a1" }}
                  onClick={() => setShowAdvanced((prev) => !prev)}
                >
                  Advanced
                  {showAdvanced ? (
                    <i class="fa fa-angle-up fa-lg" aria-hidden="true"></i>
                  ) : (
                    <i class="fa fa-angle-down fa-lg" aria-hidden="true"></i>
                  )}
                </p>
                {showAdvanced && (
                  <GoalSeek
                    order={order}
                    fetchCalculatorDataForOffers={fetchCalculatorDataForOffers}
                    fetchGetCalculator={fetchGetCalculator}
                    sendDirtyPrice={receiveDirtyPrice}
                    sendOriginalTNA={receiveOriginalTNA}
                    disabled={readonly.OrderType || (!canEditTNA &&
                      (order.OrderStatus === OrderStatus.ACKED || order.OrderStatus === OrderStatus.ROUTED || order.OrderStatus === OrderStatus.AUTH_WAIT))}
                  />
                )}
              </Col>
            )}

            {/* ##### TimeForce ###### */}
            <TimeForce
              date={order.OrderGTD}
              timeForce={order.OrderTimeInForce}
              errors={{
                date: errors.OrderGTD,
                timeForce: errors.OrderTimeInForce,
              }}
              readonly={{
                date: readonly.OrderGTD,
                timeForce: readonly.OrderTimeInForce,
              }}
              onChange={(v) => onChange(dispatch, v)}
            />

            {(ui.userRole === "Trader" || (ui.userRole !== "Trader" && ui.mode !== Mode.EDIT)) && (
              <Booktype
                booktypes={booktypes}
                booktype={order.BookType}
                balancetype={order.CurrentAccount.BalanceType}
                errors={{
                  BookType: errors.BookType,
                  BalanceType: errors.BalanceType,
                }}
                readonly={{
                  BookType: readonly.BookType,
                  BalanceType: errors.BalanceType,
                }}
                onChange={(v) => onChange(dispatch, v)}
                onAccountChange={(v) => onAccountChange(dispatch, v)}
              />
            )}
          </Col>

          {/* Right */}
          <Col xs={12} lg={6}>
            {/* ##### Account ###### */}
            {!ui.isBulk && !ui.isBasket && (
              <Account
                userType={ui.userRole}
                platformRole={platformRole}
                onChange={(v) => onAccountChange(dispatch, v)}
                balanceReady={!!order.AssetId}
                dispatch={dispatch}
                {...services.account}
                accountInfo={accountInfo}
                {...accountProps({ order, readonly, errors })}
                hiddens={{
                  Quantity: true,
                  ...hiddens,
                }}
                opsViewAccountName={opsViewAccountName}
              />
            )}
            {ui.isBulk && (
              <Bulk
                balanceReady={!!order.AssetId}
                onChange={(v) => onAccountChange(dispatch, v)}
                accounts={order.Accounts || []}
                isReadOnly={ui.mode == Mode.READONLY}
                canModifyComposition={ui.mode == Mode.EDIT}
                readonly={readonly}
                error={errors.Accounts}
                account={order.CurrentAccount}
                {...services.account}
                accountInfo={accountInfo}
                hiddens={{
                  ...hiddens,
                }}
              />
            )}

            {ui.isBasket && (
              <Basket
                balanceReady={!!order.AssetId}
                onChange={(v) => onAccountChange(dispatch, v)}
                accounts={order.Accounts || []}
                isReadOnly={ui.mode == Mode.READONLY}
                canModifyComposition={ui.mode == Mode.EDIT}
                readonly={readonly}
                error={errors.Accounts}
                account={order.CurrentAccount}
                {...services.account}
                accountInfo={accountInfo}
                toggleOrderModal={toggle}
                hiddens={{
                  ...hiddens,
                }}
              />
            )}
            {((isFromOffer && useProcessDateInOffers) || showProcessDate) && (
              <React.Fragment>
                <Label style={{ fontSize: "12px", fontWeight: 500, marginBottom: "3px" }} for="orderDate">
                  Process Date
                </Label>
                <Input
                  type="date"
                  name="date"
                  id="orderProcessDate"
                  placeholder="date placeholder"
                  onChange={(e) => onUserChangeProcessDate(e.target.value)}
                  value={orderProcessDate}
                  disabled={ui.mode != Mode.EDIT}
                  min={getTodayISO()}
                />
              </React.Fragment>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export const defaultValues = () => {
  return {
    OrderSide: "Buy",
    BookType: "Self",
  };
};

export const validations = (order, userType) => {
  const filteredBankAccounts = order.CurrentAccount.clientBankAccounts
    ? order.CurrentAccount.clientBankAccounts.filter(
        (account) => account.currencyISO === order.OrderCurrencySymbol
      )
    : [];

  const isMultiAccounts = order.OrderHasMultipleAccounts;
  return {
    OrderQuantity: { empty: !isMultiAccounts },
    AssetTicker: { empty: true, autosuggestId: "AssetId" },
    OrderType: { empty: true },
    OrderPrice: {
      empty: order.OrderType == OrderPriceType.LIMIT || order.OrderType == OrderPriceType.STOPLIMIT,
    },
    OrderStopPrice: {
      empty: order.OrderType == OrderPriceType.STOP || order.OrderType == OrderPriceType.STOPLIMIT,
    },
    OrderTimeInForce: { empty: true },
    OrderGTD: {
      empty: order.OrderTimeInForce == TimeFrame.GTD,
    },
    OrderSettlementCurrencySymbol: { empty: true },

    // The following properties are from an account.
    AccountName: {
      empty: true,
      accountLevel: true,
    },
    HolderName: {
      empty: userType !== UserType.Trader,
      accountLevel: true,
    },
    clientBankAccounts: {
      noBankAccountSelected:
        filteredBankAccounts &&
        filteredBankAccounts.length > 0 &&
        (order.CurrentAccount.clientBankAccount == null || order.CurrentAccount.clientBankAccount == "") &&
        !order.CurrentAccount.useCashAccount,
    },
    Quantity: {
      empty: true,
      accountLevel: true,
    },
    Instruction: {
      empty: userType !== UserType.Trader,
      accountLevel: true,
    },
    MarkupType: {
      empty: userType !== UserType.Trader,
      accountLevel: true,
    },
    CommissionType: {
      empty: userType !== UserType.Trader,
      accountLevel: true,
    },
  };
};

export default Bonds;
